import React, { useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { FaHome, FaGlobe, FaGavel, FaCalendar, FaWeight, FaUser, FaEye, FaDice } from 'react-icons/fa';
import ReactGA from 'react-ga4';
import '@fontsource/inter';
import HomePage from './HomePage';
import CountryRankings from './CountryRankings';
import CountryProfile from './CountryProfile';
import FighterIssueCounter from './FighterIssueCounter';
import FightOutcomeVisualizer from './FightOutcomeVisualizer';
import EventsByYear from './EventsByYear';
import ReboundsViewer from './ReboundsViewer';
import Predictors from './Predictors';
import Odds from './Odds';

function NavBar() {
    const location = useLocation();
    
    const navStyle = {
        display: 'flex',
        gap: '0.5rem',
        padding: '0.01rem 2rem',
        background: '#222',
        color: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        flexWrap: 'wrap',
        position: 'sticky',
        top: 0,
        zIndex: 1000
    };
    
    const linkStyle = {
        color: '#aaa',
        textDecoration: 'none',
        fontWeight: 400,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.5rem',
        transition: 'all 0.3s ease',
        padding: '0.5rem 0.5rem 0.5rem 0.5rem',
        borderRadius: '0px',
        position: 'relative',
        width: '100px',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid transparent'
    };

    const linkHoverStyle = {
        color: '#fff'
    };

    const [hoveredLink, setHoveredLink] = React.useState(null);

    const getLinkStyle = (linkId, itemPath) => {
        const isActive = location.pathname === itemPath;
        return {
            ...linkStyle,
            ...(hoveredLink === linkId ? linkHoverStyle : {}),
            ...(isActive ? { color: '#fff', borderBottom: '2px solid #fff' } : {})
        };
    };

    const iconStyle = {
        fontSize: '1.5rem',
        lineHeight: '1.5rem',
        filter: 'grayscale(100%)',
        display: 'block',
        textAlign: 'center',
        width: '100%',
        height: '1.5rem'
    };

    const labelStyle = {
        fontSize: '0.75rem',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        maxWidth: '100px',
        color: 'inherit',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };

    const navItems = [
        { icon: FaHome, label: 'Home', path: '/', id: 'home' },
        { icon: FaGavel, label: 'Finish Rates', path: '/outcomes', id: 'outcomes' },
        { icon: FaCalendar, label: 'Events', path: '/events', id: 'events' },
        { icon: FaDice, label: 'Odds', path: '/odds', id: 'odds' },
        { icon: FaWeight, label: 'Rebounds', path: '/rebounds', id: 'rebounds' },
        { icon: FaEye, label: 'Predictors', path: '/predictors', id: 'predictors' }
    ];

    return (
        <nav style={navStyle}>
            {/* Home Link */}
            {navItems.slice(0, 1).map(item => {
                const IconComponent = item.icon;
                return (
                    <Link 
                        key={item.id}
                        to={item.path} 
                        style={getLinkStyle(item.id, item.path)}
                        onMouseEnter={() => setHoveredLink(item.id)}
                        onMouseLeave={() => setHoveredLink(null)}
                    >
                        <div style={iconStyle}><IconComponent size={24} /></div>
                        <div style={labelStyle}>{item.label}</div>
                    </Link>
                );
            })}

            {/* Countries Link */}
            <Link
                to="/country-rankings"
                style={getLinkStyle('countries', '/country-rankings')}
                onMouseEnter={() => setHoveredLink('countries')}
                onMouseLeave={() => setHoveredLink(null)}
            >
                <div style={iconStyle}><FaGlobe size={24} /></div>
                <div style={labelStyle}>Countries</div>
            </Link>

            {/* Individuals Button for Fighter Consistency */}
            <Link
                to="/individual-rankings"
                style={getLinkStyle('individuals', '/individual-rankings')}
                onMouseEnter={() => setHoveredLink('individuals')}
                onMouseLeave={() => setHoveredLink(null)}
            >
                <div style={iconStyle}><FaUser size={24} /></div>
                <div style={labelStyle}>Individuals</div>
            </Link>

            {/* Other Links */}
            {navItems.slice(1).map(item => {
                const IconComponent = item.icon;
                return (
                    <Link 
                        key={item.id}
                        to={item.path} 
                        style={getLinkStyle(item.id, item.path)}
                        onMouseEnter={() => setHoveredLink(item.id)}
                        onMouseLeave={() => setHoveredLink(null)}
                    >
                        <div style={iconStyle}><IconComponent size={24} /></div>
                        <div style={labelStyle}>{item.label}</div>
                    </Link>
                );
            })}
        </nav>
    );
}

function App() {
    const location = useLocation();

    // Track page views
    useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: location.pathname });
    }, [location]);

    const appStyle = {
        padding: 0,
        fontFamily: 'Inter, sans-serif',
        background: '#1a1a1a',
        color: '#f1f1f1',
        minHeight: '100vh'
    };

    return (
        <div style={appStyle}>
            <NavBar />
            <main style={{padding: '2em'}}>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/country-rankings" element={<CountryRankings darkMode={true} />} />
                    <Route path="/country-rankings/:countryCode" element={<CountryProfile darkMode={true} />} />
                    <Route path="/individual-rankings" element={<FighterIssueCounter darkMode={true} />} />
                    <Route path="/outcomes" element={<FightOutcomeVisualizer />} />
                    <Route path="/events" element={<EventsByYear />} />
                    <Route path="/rebounds" element={<ReboundsViewer />} />
                    <Route path="/predictors" element={<Predictors />} />
                    <Route path="/odds" element={<Odds />} />
                </Routes>
            </main>
        </div>
    );
}

export default App;
